// src/components/SyncProgressBar/SyncProgressBar.js

import { Component, h, render } from '@monygroupcorp/microact';

/**
 * SyncProgressBar - UI component showing indexer sync status.
 *
 * @example
 * import { SyncProgressBar } from '@monygroupcorp/micro-web3/components';
 * render(h(SyncProgressBar, { indexer, eventBus }), document.getElementById('sync-status'));
 */
class SyncProgressBar extends Component {
    constructor(props) {
        super(props);

        if (!props.indexer) {
            throw new Error('SyncProgressBar requires indexer instance');
        }
        if (!props.eventBus) {
            throw new Error('SyncProgressBar requires eventBus instance');
        }

        this.indexer = props.indexer;
        this.eventBus = props.eventBus;

        this.state = {
            status: 'initializing',
            progress: 0,
            currentBlock: 0,
            latestBlock: 0,
            error: null
        };
    }

    didMount() {
        this.subscribe('indexer:syncProgress', (data) => {
            this.setState({
                status: 'syncing',
                progress: data.progress,
                currentBlock: data.currentBlock,
                latestBlock: data.latestBlock
            });
        });

        this.subscribe('indexer:syncComplete', () => {
            this.setState({ status: 'synced', progress: 1 });
        });

        this.subscribe('indexer:error', (data) => {
            this.setState({ status: 'error', error: data.message });
        });

        this.subscribe('indexer:paused', () => {
            this.setState({ status: 'paused' });
        });

        this.subscribe('indexer:resumed', () => {
            this.setState({ status: 'syncing' });
        });

        // Initial status
        const initialStatus = this.indexer.sync?.getStatus?.();
        if (initialStatus) {
            this.setState(initialStatus);
        }
    }

    handleRetry() {
        this.indexer.sync?.resync?.();
    }

    handleResume() {
        this.indexer.sync?.resume?.();
    }

    render() {
        const { status, progress, currentBlock, latestBlock, error } = this.state;
        const percent = Math.round(progress * 100);

        return h('div', { className: `mw3-sync-progress mw3-sync-progress--${status}` },
            status === 'syncing' && [
                h('div', {
                    key: 'bar',
                    className: 'mw3-sync-progress__bar',
                    style: { width: `${percent}%` }
                }),
                h('span', { key: 'text', className: 'mw3-sync-progress__text' }, `Syncing... ${percent}%`)
            ],

            status === 'synced' &&
                h('span', { className: 'mw3-sync-progress__text' },
                    `Synced to block ${latestBlock.toLocaleString()}`
                ),

            status === 'error' && [
                h('span', { key: 'text', className: 'mw3-sync-progress__text' },
                    `Sync failed: ${error || 'Unknown error'}`
                ),
                h('button', {
                    key: 'retry',
                    className: 'mw3-sync-progress__retry',
                    onClick: this.bind(this.handleRetry)
                }, 'Retry')
            ],

            status === 'paused' && [
                h('span', { key: 'text', className: 'mw3-sync-progress__text' }, 'Sync paused'),
                h('button', {
                    key: 'resume',
                    className: 'mw3-sync-progress__resume',
                    onClick: this.bind(this.handleResume)
                }, 'Resume')
            ],

            status === 'initializing' &&
                h('span', { className: 'mw3-sync-progress__text' }, 'Initializing...')
        );
    }

    static get styles() {
        return `
            .mw3-sync-progress {
                position: relative;
                height: 24px;
                background: var(--mw3-sync-bg, #f0f0f0);
                border-radius: 4px;
                overflow: hidden;
                font-family: system-ui, sans-serif;
                font-size: 12px;
            }

            .mw3-sync-progress__bar {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                background: var(--mw3-sync-bar, #4caf50);
                transition: width 0.3s ease;
            }

            .mw3-sync-progress__text {
                position: relative;
                z-index: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: var(--mw3-sync-text, #333);
                padding: 0 8px;
            }

            .mw3-sync-progress--synced {
                background: var(--mw3-sync-bar, #4caf50);
            }

            .mw3-sync-progress--synced .mw3-sync-progress__text {
                color: white;
            }

            .mw3-sync-progress--error {
                background: var(--mw3-sync-error, #f44336);
            }

            .mw3-sync-progress--error .mw3-sync-progress__text {
                color: white;
                justify-content: space-between;
            }

            .mw3-sync-progress__retry,
            .mw3-sync-progress__resume {
                background: rgba(255,255,255,0.2);
                border: 1px solid rgba(255,255,255,0.4);
                border-radius: 3px;
                color: white;
                padding: 2px 8px;
                cursor: pointer;
                font-size: 11px;
            }

            .mw3-sync-progress__retry:hover,
            .mw3-sync-progress__resume:hover {
                background: rgba(255,255,255,0.3);
            }

            .mw3-sync-progress--paused {
                background: var(--mw3-sync-paused, #ff9800);
            }

            .mw3-sync-progress--paused .mw3-sync-progress__text {
                color: white;
                justify-content: space-between;
            }
        `;
    }
}

export { SyncProgressBar };
